import React, { useEffect, useRef, useState } from "react";
import { supabase } from "../SupabaseClient.tsx";
import "./LandingPage.css";

declare global {
  interface Window {
    handleSignInWithGoogle: (response: any) => Promise<void>;
    google: any;
  }
}

interface LandingPageProps {
  // Open on the sign-up form, for people arriving from a free trial button
  startInSignUp?: boolean;
}

const LandingPage: React.FC<LandingPageProps> = ({ startInSignUp = false }) => {
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [isSignUp, setIsSignUp] = useState<boolean>(startInSignUp);
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [signInError, setSignInError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  // The rules take up most of a phone screen, and most people sign up with Google
  const [showPasswordRules, setShowPasswordRules] = useState<boolean>(false);
  const [signUpSuccess, setSignUpSuccess] = useState<boolean>(false);
  const [verificationEmail, setVerificationEmail] = useState<string>("");
  // Add states for reset password functionality
  const [isForgotPassword, setIsForgotPassword] = useState<boolean>(false);
  const [resetPasswordSuccess, setResetPasswordSuccess] =
    useState<boolean>(false);
  const [resetPasswordEmail, setResetPasswordEmail] = useState<string>("");
  // Add states for expired verification link
  const [isVerificationExpired, setIsVerificationExpired] =
    useState<boolean>(false);
  const [resendVerificationSuccess, setResendVerificationSuccess] =
    useState<boolean>(false);

  // Add state for tracking individual password requirements
  const [passwordRequirements, setPasswordRequirements] = useState({
    hasLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSymbol: false,
    passwordsMatch: false,
  });

  // Check if all requirements are met for signup button enablement
  const allRequirementsMet = isSignUp
    ? Object.values(passwordRequirements).every((req) => req === true)
    : true;

  useEffect(() => {
    // Check for expired verification link in URL
    const hash = window.location.hash;
    if (
      hash.includes("error=access_denied") &&
      hash.includes("error_code=otp_expired") &&
      hash.includes("error_description=Email+link+is+invalid+or+has+expired")
    ) {
      setIsVerificationExpired(true);
      // Clean up the URL
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);

  useEffect(() => {
    // Load Google Identity Services script
    const loadGoogleScript = () => {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.onload = () => {
        if (window.google && buttonRef.current) {
          window.google.accounts.id.initialize({
            client_id:
              "770499561610-bn7l337pafvj7uf68pn76hhaguvclt2j.apps.googleusercontent.com",
            callback: window.handleSignInWithGoogle,
          });

          window.google.accounts.id.renderButton(buttonRef.current, {
            type: "standard",
            shape: "rectangular",
            theme: "outline",
            text: "continue_with",
            size: "large",
            logo_alignment: "left",
            width: 280,
          });
        }
      };
      document.body.appendChild(script);
    };

    loadGoogleScript();

    return () => {
      // Cleanup if needed
      const scripts = document.querySelectorAll(
        'script[src="https://accounts.google.com/gsi/client"]'
      );
      scripts.forEach((script) => script.remove());
    };
  }, []);

  window.handleSignInWithGoogle = async function (response) {
    const { error } = await supabase.auth.signInWithIdToken({
      provider: "google",
      token: response.credential,
    });

    if (error) {
      console.error("Error signing in with Google:", error);
      setSignInError(
        "Google sign-in didn't work. Please try again, or use your email below."
      );
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      setEmailError("Email is required");
      return false;
    } else if (!emailRegex.test(email)) {
      setEmailError("Please enter a valid email address");
      return false;
    }
    setEmailError("");
    return true;
  };

  // Helper functions to check individual password requirements
  const checkPasswordRequirements = (
    password: string,
    confirmPwd: string = confirmPassword
  ) => {
    setPasswordRequirements({
      hasLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSymbol: /[#?!@$%^&*]/.test(password),
      passwordsMatch: password === confirmPwd && password.length > 0,
    });
  };

  const validatePassword = (password: string): boolean => {
    // Password must be at least 8 characters and include uppercase, lowercase, number, and symbol
    const passwordRegex =
      /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*]).{8,}$/;

    if (!password) {
      setPasswordError("Password is required");
      return false;
    } else if (!passwordRegex.test(password)) {
      setPasswordError(
        "Password must be at least 8 characters and include uppercase, lowercase, number, and symbol (#?!@$%^&*)"
      );
      return false;
    }
    setPasswordError("");
    return true;
  };

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setSignInError("");
    setSignUpSuccess(false);
    setResetPasswordSuccess(false);

    if (!validateEmail(email)) return;

    // Handle forgot password submission
    if (isForgotPassword) {
      setLoading(true);
      try {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/update-password`,
        });

        if (error) throw error;

        setResetPasswordSuccess(true);
        setResetPasswordEmail(email);
      } catch (error: any) {
        console.error("Password reset error:", error);
        setSignInError(
          error.message || "An error occurred during password reset"
        );
      } finally {
        setLoading(false);
      }
      return;
    }

    if (isSignUp) {
      if (!validatePassword(password)) return;
      if (password !== confirmPassword) {
        setPasswordError("Passwords do not match");
        return;
      }
    }

    setLoading(true);

    try {
      if (isSignUp) {
        // Sign up
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            emailRedirectTo: window.location.origin,
          },
        });

        if (error) throw error;

        // Set success message and store email for display
        setSignUpSuccess(true);
        setVerificationEmail(email);

        // Reset form fields
        setPassword("");
        setConfirmPassword("");

        // Switch back to sign-in mode
        setIsSignUp(false);
      } else {
        // Sign in
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;
      }
    } catch (error: any) {
      console.error("Authentication error:", error);
      setSignInError(
        error.message || "An error occurred during authentication"
      );
    } finally {
      setLoading(false);
    }
  };

  // Update password input handler to check requirements in real time
  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    if (isSignUp) {
      checkPasswordRequirements(newPassword, confirmPassword);
    }
  };

  // Handle confirm password changes to check match in real time
  const handleConfirmPasswordChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newConfirmPassword = e.target.value;
    setConfirmPassword(newConfirmPassword);
    if (isSignUp) {
      checkPasswordRequirements(password, newConfirmPassword);
    }
  };

  const toggleAuthMode = () => {
    setIsSignUp(!isSignUp);
    setEmailError("");
    setPasswordError("");
    setSignInError("");
    setSignUpSuccess(false);
    setResetPasswordSuccess(false);
    setIsForgotPassword(false);
    setPassword("");
    setConfirmPassword("");

    // Reset password requirement checklist
    setPasswordRequirements({
      hasLength: false,
      hasUppercase: false,
      hasLowercase: false,
      hasNumber: false,
      hasSymbol: false,
      passwordsMatch: false,
    });
  };

  // Add handler for forgot password toggle
  const toggleForgotPassword = () => {
    setIsForgotPassword(!isForgotPassword);
    setEmailError("");
    setPasswordError("");
    setSignInError("");
    setSignUpSuccess(false);
    setResetPasswordSuccess(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleResendVerification = async () => {
    if (!validateEmail(email)) return;

    setLoading(true);
    setSignInError("");
    setResendVerificationSuccess(false);

    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email,
        options: {
          emailRedirectTo: window.location.origin,
        },
      });

      if (error) throw error;

      setResendVerificationSuccess(true);
      setVerificationEmail(email);
      setIsVerificationExpired(false);
    } catch (error: any) {
      console.error("Error resending verification:", error);
      setSignInError(
        error.message || "An error occurred while resending verification email"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="landing-page">
      <div className="landing-container">
        <div className="logo">
          <a href="https://shoebox.io">
            <img src="/logoHorizontal.svg" alt="Shoebox" />
          </a>
        </div>
        <div className="landing-content">
          <div className="sign-in-section">
            <h3>
              {isForgotPassword
                ? "Password Reset"
                : isSignUp
                ? "Start your free trial"
                : "Sign In"}
            </h3>

            {isSignUp && (
              <p className="sign-up-terms">
                30 days free, no credit card required. $60/year if you choose to
                subscribe.
              </p>
            )}

            {/* Show verification message after successful sign-up */}
            {signUpSuccess && (
              <div className="success-message">
                <p>
                  We've sent a verification link to{" "}
                  <strong>{verificationEmail}</strong>.
                </p>
                <p>
                  Please check your email and click the link to complete your
                  registration. If you don't see the email within 5 minutes, be
                  sure to check your spam folder.
                </p>
              </div>
            )}

            {/* Show reset password success message */}
            {resetPasswordSuccess && (
              <div className="success-message">
                <p>
                  We've sent a password reset link to{" "}
                  <strong>{resetPasswordEmail}</strong>.
                </p>
                <p>
                  Please check your email and click the link to reset your
                  password.
                </p>
                <p>You'll be redirected to update your password.</p>
              </div>
            )}

            {/* Show expired verification link message */}
            {isVerificationExpired && !resendVerificationSuccess && (
              <div className="expired-verification-message">
                <p>
                  <strong>
                    Your verification link has expired or is invalid.
                  </strong>
                </p>
                <p>
                  Please enter your email below and click "Resend Verification"
                  to receive a new verification link.
                </p>
              </div>
            )}

            {/* Show resend verification success message */}
            {resendVerificationSuccess && (
              <div className="success-message">
                <p>
                  We've sent a new verification link to{" "}
                  <strong>{verificationEmail}</strong>.
                </p>
                <p>
                  Please check your email and click the link to complete your
                  registration. If you don't see the email within 5 minutes, be
                  sure to check your spam folder.
                </p>
              </div>
            )}

            {/* Kept mounted when hidden, because Google only draws its button once */}
            <div
              hidden={
                isForgotPassword ||
                isVerificationExpired ||
                resetPasswordSuccess
              }
            >
              <div className="sign-in-options">
                <div ref={buttonRef} className="g_id_signin"></div>
              </div>
              <div className="divider">
                <span>OR</span>
              </div>
            </div>

            {/* Email Authentication Form */}
            <form onSubmit={handleEmailSignIn} className="email-auth-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
                {emailError && (
                  <div className="error-message">{emailError}</div>
                )}
              </div>

              {!isForgotPassword && !isVerificationExpired && (
                <div className="form-group">
                  <div className="password-label-row">
                    <label htmlFor="password">Password</label>
                    {/* Add Forgot Password link when in sign in mode */}
                    {!isSignUp && (
                      <button
                        type="button"
                        className="forgot-password-link"
                        onClick={toggleForgotPassword}
                      >
                        Forgot Password?
                      </button>
                    )}
                  </div>
                  <div className="password-input-container">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      value={password}
                      onChange={handlePasswordChange}
                      onFocus={() => setShowPasswordRules(true)}
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password-visibility"
                      onClick={togglePasswordVisibility}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                  {passwordError && (
                    <div className="error-message">{passwordError}</div>
                  )}
                </div>
              )}

              {isSignUp && !isForgotPassword && (
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password</label>
                  <div className="password-input-container">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={handleConfirmPasswordChange}
                      placeholder="Confirm your password"
                      required
                    />
                    <button
                      type="button"
                      className="toggle-password-visibility"
                      onClick={toggleConfirmPasswordVisibility}
                      aria-label={
                        showConfirmPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showConfirmPassword ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>
              )}

              {/* Password requirements checklist - only show when signing up */}
              {isSignUp && showPasswordRules && (
                <div className="password-requirements">
                  <div
                    className={`requirement ${
                      passwordRequirements.hasLength ? "met" : ""
                    }`}
                  >
                    <span className="indicator">
                      {passwordRequirements.hasLength ? "✓" : "○"}
                    </span>
                    At least 8 characters
                  </div>
                  <div
                    className={`requirement ${
                      passwordRequirements.hasUppercase ? "met" : ""
                    }`}
                  >
                    <span className="indicator">
                      {passwordRequirements.hasUppercase ? "✓" : "○"}
                    </span>
                    At least one uppercase letter
                  </div>
                  <div
                    className={`requirement ${
                      passwordRequirements.hasLowercase ? "met" : ""
                    }`}
                  >
                    <span className="indicator">
                      {passwordRequirements.hasLowercase ? "✓" : "○"}
                    </span>
                    At least one lowercase letter
                  </div>
                  <div
                    className={`requirement ${
                      passwordRequirements.hasNumber ? "met" : ""
                    }`}
                  >
                    <span className="indicator">
                      {passwordRequirements.hasNumber ? "✓" : "○"}
                    </span>
                    At least one number
                  </div>
                  <div
                    className={`requirement ${
                      passwordRequirements.hasSymbol ? "met" : ""
                    }`}
                  >
                    <span className="indicator">
                      {passwordRequirements.hasSymbol ? "✓" : "○"}
                    </span>
                    At least one special character (#?!@$%^&*)
                  </div>
                  <div
                    className={`requirement ${
                      passwordRequirements.passwordsMatch ? "met" : ""
                    }`}
                  >
                    <span className="indicator">
                      {passwordRequirements.passwordsMatch ? "✓" : "○"}
                    </span>
                    Passwords match
                  </div>
                </div>
              )}

              {signInError && (
                <div className="error-message signin-error">{signInError}</div>
              )}

              {/* Show Resend Verification button when verification expired */}
              {isVerificationExpired && !resendVerificationSuccess ? (
                <button
                  type="button"
                  className="auth-button"
                  onClick={handleResendVerification}
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Resend Verification"}
                </button>
              ) : (
                <button
                  type="submit"
                  className="auth-button"
                  disabled={
                    loading ||
                    (isSignUp && !isForgotPassword && !allRequirementsMet)
                  }
                >
                  {loading
                    ? "Processing..."
                    : isForgotPassword
                    ? "Reset Password"
                    : isSignUp
                    ? "Sign Up"
                    : "Sign In"}
                </button>
              )}

              <div className="auth-toggle">
                {isForgotPassword ? (
                  <button
                    type="button"
                    onClick={toggleForgotPassword}
                    className="toggle-button"
                  >
                    Back to sign in
                  </button>
                ) : (
                  <>
                    <span className="auth-toggle-text">
                      {isSignUp
                        ? "Already have an account? "
                        : "Don't have an account? "}
                    </span>
                    <button
                      type="button"
                      onClick={toggleAuthMode}
                      className="toggle-button"
                    >
                      {isSignUp ? "Sign In" : "Sign Up"}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>

          <div className="landing-footer">
            <p>
              By continuing, you agree to our{" "}
              <a href="/terms">Terms of Service</a> and{" "}
              <a href="/privacy-policy">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>

      {/* Hidden for safety */}
      <div
        id="g_id_onload"
        data-client_id="770499561610-bn7l337pafvj7uf68pn76hhaguvclt2j.apps.googleusercontent.com"
        data-context="signin"
        data-ux_mode="popup"
        data-callback="handleSignInWithGoogle"
        data-auto_select="true"
        data-close_on_tap_outside="false"
        data-itp_support="true"
      ></div>
    </div>
  );
};

export default LandingPage;
